import { Box, Card, Typography, useTheme } from "@mui/material";
import BoatImage from "assets/images/boat.png";
import { Item } from "lib/features/item/types";
import {
  CreateReferralDto,
  useCreateReferralMutation,
} from "lib/features/referral/referralService";
import { selectUser } from "lib/features/user/userSlice";
import { useAppSelector } from "lib/hooks";
import moment from "moment";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { availabilityCardStyles } from "styles/Home/availabilityCard.styles";
import { formatDurationTime } from "util/commonHelper";
import { parseImageUrl } from "util/parseImageUrl";
import clock from "../../../assets/icons/clock_black.svg";
import DollarSign from "../../../assets/icons/dollar.svg";
import members from "../../../assets/icons/members_black.svg";
import CreateReferralModal from "./CreateReferralModal";
import { AvailabilityCardProps } from "./types";

const AvailabilityCard = ({
  item,
  referredBy,
  user_id,
}: AvailabilityCardProps) => {
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const theme = useTheme();

  const [modalOpen, setModalOpen] = useState(false);
  const [createReferral, { isLoading }] = useCreateReferralMutation();

  const fallbackImage = parseImageUrl(item?.itemImages[0].uri) || BoatImage;

  const handleCreateReferral = async () => {
    if (isLoading) return;

    const userId = referredBy && referredBy > 0 ? referredBy : user?.id ?? 0;

    const newReferral: CreateReferralDto = {
      user_id: user_id ? user_id : userId,
      business_id: item?.business?.id ?? 0,
      availability_id: item?.availabilities[0]?.id,
      item_id: Number(item.id),
      referral_type: 1,
      state: 1,
    };
    
    try {
      setModalOpen(true);
      const response = await createReferral(newReferral).unwrap();
      setModalOpen(false);

      const checkoutUrl = user_id
        ? `/checkout/${response?.id}?showBack=${true}`
        : `/checkout/${response?.id}`;

      window.open(checkoutUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      setModalOpen(false);
      console.log("Failed to create referral: ", error);
    }
  };

  const showMaxGuest = useMemo(() => {
    if (item?.itemDetails[0]?.max_guest) {
      return item?.itemDetails[0]?.max_guest;
    }
  }, [item?.itemDetails[0]?.capacity, item?.itemDetails[0]?.max_guest]);

  const getUnitName = (itemDetail:Item) => {
    // console.log('Getting unit name for item:', itemDetail?.itemDetails[0]?.unit_type);
    const unitType = itemDetail?.itemDetails[0]?.unit_type;
    return "each"; // Default value
    if (unitType === 1) return "Person";
    if (unitType === 2) return "Boat";
    if (unitType === 3) return "Group";
    return "Person"; 
  };

  const getBusinessName = () => {
    if (typeof item?.business === 'string') {
      return item.business;
    }
    return item?.business?.business || "";
  };

  const getHeadline = () => {
    return item?.itemDetails?.[0]?.headline || item?.business_headline || "";
  };

  return (
    <>
      <Card
        sx={availabilityCardStyles.card(theme)}
        onClick={() => {
          if (!isLoading) {
            handleCreateReferral();
          }
        }}
      >
        <Box sx={availabilityCardStyles.imageBox}>
          {!referredBy && item.itemDetails?.[0]?.comission && (
            <Box
              sx={{
                position: "absolute",
                top: 12,
                left: 12,
                display: "flex",
                alignItems: "center",
                backgroundColor: "white",
                borderRadius: "15px",
                padding: "2px 5px",
              }}
            >
              <img
                src={DollarSign}
                alt="commission icon"
                style={{ width: "16px", height: "16px" }}
              />
              <Typography
                style={{
                  fontSize: "12px",
                  marginLeft: 4,
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: "600",
                }}
              >
                Pays {item.itemDetails[0].comission}%
              </Typography>
            </Box>
          )}

          <img
            src={fallbackImage}
            alt={"item-image"}
            style={availabilityCardStyles.image}
          />
        </Box>

        <Box sx={availabilityCardStyles.detailsBox}>
          {item?.itemDetails?.[0]?.item_name && (
            // this is main heading
            <Typography 
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 700,
                fontSize: "17px",
                lineHeight: "28px",
                color: "#000000",
                marginBottom: "0px",
              }}
            >
              {item?.itemDetails?.[0]?.item_name}
            </Typography>
          )}

          <Box sx={{ minHeight: "22px", marginBottom: "4px" }}>
            {/* this is subheading */}
            <Typography 
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "15px",
                fontWeight: 400,
                lineHeight: "22px",
                color: "#000000",
                marginBottom: "0px",
              }}
            >
              {getBusinessName() || "Business Name"}
            </Typography>
          </Box>

          <Box 
            sx={{
              minHeight: "auto",
              marginBottom: "8px",
            }}
          >
            {/* this is description */}
            <Typography 
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: "11px",
                fontWeight: 400,
                lineHeight: "20px",
                color: "#666666",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {getHeadline()}
            </Typography>
          </Box>
          <>
            <Box sx={availabilityCardStyles.infoSection(theme)}>
              {/* this is price */}
              {item.itemDetails[0]?.price && (
                <Typography 
                  sx={{
                    ...availabilityCardStyles.infoTypography(theme),
                    fontWeight: 600,
                    fontSize: "15px",
                  }}
                >
                  ${item.itemDetails[0]?.price}{'/'}{getUnitName(item)} 
                  {/* {item.itemDetails[0]?.unit_type && `/${getUnitName(item)}`}  */}
                </Typography>
              )}

              {item.itemDetails?.[0]?.max_guest !==undefined  && item.itemDetails?.[0]?.max_guest > 0 && (
                <Typography 
                  sx={{
                    ...availabilityCardStyles.infoTypography(theme),
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontSize: "15px",
                    fontWeight: '400'
                  }}
                >
                  <img
                    src={members}
                    alt="members icon"
                    style={{ width: "16px", height: "16px" }}
                  />
                  {showMaxGuest}{' '}max
                </Typography>
              )}

              <Typography 
                sx={{
                  ...availabilityCardStyles.infoTypography(theme),
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  fontSize: "15px",
                  fontWeight: '400'
                }}
              >
                <img
                  src={clock}
                  alt="clock icon"
                  style={{ width: "16px", height: "16px" }}
                />
                {item.itemDetails?.[0]?.duration_applicable &&
                item.itemDetails[0]?.duration
                  ? formatDurationTime(item?.itemDetails?.[0]?.duration)
                  : 0}
                hr
              </Typography>
            </Box>

            {item?.availabilities?.length > 0 && (
              <Box sx={availabilityCardStyles.timeSlotsContainer}>
                <Box sx={availabilityCardStyles.timeSlotsGrid(theme)}>
                  {item.availabilities
                    ?.slice(0, 3)
                    .map((availability: any, index: number) => (
                      // this box is each time slot
                      <Box
                        key={index}
                        sx={availabilityCardStyles.timeSlotBox(theme)}
                      >
                        <Typography
                          sx={{
                            ...availabilityCardStyles.timeSlotTime(theme),
                            fontWeight: 500,
                            fontSize: "14px",
                            lineHeight: "20px",
                          }}
                        >
                          {moment
                            .utc(availability.start_time)
                            .local()
                            .format("h:mm A")}
                        </Typography>
                        <Typography
                          sx={availabilityCardStyles.timeSlotAvailability}
                        >
                          {item.itemDetails[0]?.capacity + " available"}
                        </Typography>
                      </Box>
                    ))}
                </Box>

                {item.availabilities && item.availabilities.length > 3 && (
                  // this is number of items available line in bottom
                  <Typography sx={availabilityCardStyles.moreTimesText}>
                    {item.availabilities.length === 4
                      ? `${Math.max(0, item.availabilities.length - 3)} more time available`
                      : `${Math.max(0, item.availabilities.length - 3)} more times available`}
                  </Typography>
                )}
              </Box>
            )}
          </>

          <Typography sx={availabilityCardStyles.bookNowText(theme)}>
            {isLoading ? "Redirecting..." : ""}
          </Typography>
        </Box>
      </Card>

      <CreateReferralModal
        open={modalOpen || isLoading}
        close={() => setModalOpen(false)}
      />
    </>
  );
};

export default AvailabilityCard;